import React, { useState, useMemo, useEffect } from 'react';
import { Box, Tab, Tabs, Paper, ToggleButton, ToggleButtonGroup, Button, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import RefreshIcon from '@mui/icons-material/Refresh';
import { capitalize } from '../../../../common/utils/common.util';
import { useMonitoringReportShared } from '../context/MonitoringReportContext';

// Import all the note components
import CoverPage from './notes/cover-page/CoverPage';
import EditableCoverPage from './notes/cover-page/EditableCoverPage';
import CoverPagePreview from './notes/cover-page/CoverPagePreview';
import EditableExecutiveSummary from './notes/executive-summary/EditableExecutiveSummary';
import ActionItemsEditor from './notes/executive-summary/ActionItemsEditor';
import ActionItemsPreview from './notes/executive-summary/ActionItemsPreview';
import ExecutiveSummaryPreview from './notes/executive-summary/ExecutiveSummaryPreview';
import LimitationsDisclosuresPreview from './notes/limitations-disclosures/LimitationsDisclosuresPreview';
import { buildMagicActionItemsHtml } from '../../magic-report/sections/action-items.util';
import { roundCompletionPercent } from '../utils/percent.util';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role='tabpanel' hidden={value !== index} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const NotesStep: React.FC = () => {
  const { reportState, setReportState, setIsDirty, reportType } = useMonitoringReportShared() as any;
  const reportData = {
    projectSummary: reportState.projectSummary,
    drawSummary: reportState.drawSummary,
    budgetSummary: reportState.budgetSummary,
    siteConditions: reportState.siteConditions,
    coverPage: reportState.coverPage,
    sections: reportState.sections,
    author: reportState.author,
  };
  const summary = reportState.summary || {};
  const [currentTab, setCurrentTab] = useState(0);
  const [viewMode, setViewMode] = useState<'preview' | 'edit'>('edit');
  const [refreshCounter, setRefreshCounter] = useState(0);

  const coverPageContent = summary?.coverLetter || '';
  const executiveSummaryContent = summary?.executiveSummary || '';
  const derivedMagicActionItems = useMemo(() => {
    if (reportType !== 'magic') return '';
    return buildMagicActionItemsHtml(
      reportState.sections,
      reportState.draws,
      (reportState as any).previousFundingsComment,
      (reportState as any).hiddenCoLienDrawIds,
    );
  }, [reportState.sections, reportType, (reportState as any).previousFundingsComment]);

  const actionItems = reportType === 'magic' ? derivedMagicActionItems : summary?.actionItems || '';

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    // Force preview mode for Limitations & Disclosures tab
    if (newValue === 3) {
      setViewMode('preview');
    } else if (newValue === 2 && reportType === 'magic') {
      // Action Items are derived from bookmarks in Magic Report
      setViewMode('preview');
    } else {
      // Force edit mode for Cover Page / Executive Summary / Action Items tabs
      setViewMode('edit');
    }
  };

  const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newMode: 'preview' | 'edit') => {
    if (newMode !== null) {
      // Show warning if trying to edit Limitations & Disclosures tab
      if (currentTab === 3 && newMode === 'edit') {
        toast.warn('Limitations and Disclosures cannot be edited.');
        return;
      }
      if (currentTab === 2 && newMode === 'edit' && reportType === 'magic') {
        toast.warn('Action Items are generated from bookmarks and cannot be edited.');
        return;
      }
      setViewMode(newMode);
    }
  };

  const { author } = reportData;

  const processedReportData = useMemo(() => {
    const { projectSummary, drawSummary, budgetSummary } = reportData;
    if (!projectSummary) return null;

    return {
      date: format(new Date(), 'MM/dd/yyyy'),
      client: {
        name: projectSummary.loanOfficerName || '',
        bank: `${projectSummary.client?.name || ''}`,
        address: projectSummary.client?.address?.street || '',
        city: capitalize(projectSummary.client?.address?.city) || '',
        state: projectSummary.client?.address?.state || '',
        zip: projectSummary.client?.address?.postalCode || '',
      },
      project: {
        name: `${projectSummary.projectNumber} - ${projectSummary.projectTitle}`,
        number: projectSummary.projectNumber || '',
        location: [
          projectSummary.projectAddress?.street,
          capitalize(projectSummary.projectAddress?.city) || null,
          projectSummary.projectAddress?.state,
          projectSummary.projectAddress?.postalCode,
        ]
          .filter(Boolean)
          .join(', '),
        observer: projectSummary?.observer ? projectSummary?.observer?.firstName + ' ' + projectSummary?.observer?.lastName : '[observer]',
      },
      draw: {
        name: drawSummary?.name || '[draw name]',
        applicationNumber: drawSummary?.applicationNumber || '[application number]',
      },
      payment: {
        requestedAmount:
          budgetSummary?.totalAmountReccForFunding != null
            ? budgetSummary.totalAmountReccForFunding.toLocaleString()
            : '[requested amount]',
        contractor: projectSummary.contractor || '',
        application: drawSummary?.applicationNumber || '[application number]',
      },
      completion: {
        percentage: roundCompletionPercent(budgetSummary?.totalContractBudgetPerComplete || 0),
        contract: drawSummary?.name || '[draw name]',
      },
      author: author
        ? {
            name: author.name || 'CDCC Representative',
            signatureUrl: author.signatureUrl,
            email: author.email,
          }
        : {
            name: 'CDCC Representative',
            signatureUrl: undefined,
            email: undefined,
          },
    } as const;
  }, [reportData]);

  const updateSummary = (partial: Partial<typeof summary>) => {
    setIsDirty(true);
    setReportState((prev: any) => ({ ...prev, summary: { ...(prev.summary || {}), ...partial } }));
  };

  const onCoverPageChange = (content: string) => updateSummary({ coverLetter: content });
  const onExecutiveSummaryChange = (content: string) => updateSummary({ executiveSummary: content });
  const onActionItemsChange = (actionItems: string) => updateSummary({ actionItems });

  // Keep summary.actionItems in sync for Magic Report so export uses the derived content.
  React.useEffect(() => {
    if (reportType !== 'magic') return;
    const current = (reportState.summary?.actionItems || '').trim();
    const next = (derivedMagicActionItems || '').trim();
    if (current === next) return;
    setReportState((prev: any) => ({ ...prev, summary: { ...(prev.summary || {}), actionItems: derivedMagicActionItems } }));
  }, [derivedMagicActionItems, reportState.summary?.actionItems, reportType, setReportState]);

  // Regenerate cover letter and executive summary from current data on mount
  // so stale saved HTML never shows incorrect budget values.
  useEffect(() => {
    const hadSavedContent = !!(reportState.summary?.coverLetter || reportState.summary?.executiveSummary);
    setReportState((prev: any) => ({
      ...prev,
      summary: { ...(prev.summary || {}), coverLetter: '', executiveSummary: '' },
    }));
    setRefreshCounter(prev => prev + 1);
    if (hadSavedContent) setIsDirty(true);
  }, []); // mount-only: intentionally regenerates from current budget data each visit

  const handleRefreshTemplates = () => {
    setReportState((prev: any) => ({
      ...prev,
      summary: { ...(prev.summary || {}), coverLetter: '', executiveSummary: '', actionItems: prev.summary?.actionItems || '' },
    }));
    setIsDirty(true);
    setRefreshCounter(prev => prev + 1);
    toast.success('Templates refreshed with latest data');
  };

  if (!processedReportData) {
    return <Box>Loading report data...</Box>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label='report sections'
            sx={{
              flex: 1, // This will make tabs take available space
              '.MuiTab-root': {
                '&:hover': {
                  backgroundColor: '#eaf5f7',
                },
                fontWeight: 'bold',
              },
            }}
          >
            <Tab label='Cover Page' />
            <Tab label='Executive Summary' />
            <Tab label='Action Items' />
            <Tab label='Limitations & Disclosures' />
            {/* Wrap the refresh button with Tooltip */}
            <Tooltip
              title={
                viewMode === 'preview'
                  ? 'You are currently in preview mode. Go to edit mode to refresh template.'
                  : 'This will update cover page and executive summary with the latest data. Action items will not be refreshed.'
              }
              arrow
            >
              <span>
                {' '}
                {/* Wrapper needed for disabled button tooltip */}
                <Button
                  onClick={handleRefreshTemplates}
                  startIcon={<RefreshIcon />}
                  size='small'
                  disabled={viewMode === 'preview'}
                  sx={{
                    minHeight: '48px', // Match tab height
                    ml: 1, // Add some spacing from the last tab
                    // Removed hover effect
                  }}
                >
                  Refresh Templates
                </Button>
              </span>
            </Tooltip>
          </Tabs>

          {/* Move toggle buttons to the end */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 2 }}>
            <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} aria-label='view mode' size='small'>
              <ToggleButton value='edit'>Edit</ToggleButton>
              <ToggleButton value='preview'>Preview</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Paper>

      <TabPanel value={currentTab} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          {viewMode === 'preview' ? (
            coverPageContent ? (
              <CoverPagePreview key={`preview-${refreshCounter}`} content={coverPageContent} executiveSummary={executiveSummaryContent} />
            ) : (
              <CoverPage key={`template-${refreshCounter}`} {...processedReportData} />
            )
          ) : (
            <EditableCoverPage
              key={`editable-${refreshCounter}`}
              initialData={processedReportData}
              reportData={reportData}
              initialHtml={coverPageContent}
              onChange={onCoverPageChange}
            />
          )}
        </Box>
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          {viewMode === 'preview' ? (
            <ExecutiveSummaryPreview key={`preview-${refreshCounter}`} executiveSummary={executiveSummaryContent} actionItems={''} />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <EditableExecutiveSummary
                key={`editable-${refreshCounter}`}
                initialData={{
                  project: {
                    name: processedReportData.project.name,
                    number: processedReportData.project.number,
                    observer: processedReportData.project.observer,
                  },
                  payment: {
                    requestedAmount: processedReportData.payment.requestedAmount,
                    contractor: processedReportData.payment.contractor,
                    application: processedReportData.payment.application,
                  },
                  completion: {
                    percentage: processedReportData.completion.percentage,
                  },
                }}
                reportData={reportData}
                initialHtml={executiveSummaryContent}
                onChange={onExecutiveSummaryChange}
                reportType={reportType}
              />
            </Box>
          )}
        </Box>
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          {viewMode === 'preview' ? (
            <ActionItemsPreview key={`preview-action-items-${refreshCounter}`} actionItems={actionItems} />
          ) : (
            <ActionItemsEditor key={`action-items-${refreshCounter}`} actionItems={actionItems} onChange={onActionItemsChange} />
          )}
        </Box>
      </TabPanel>

      <TabPanel value={currentTab} index={3}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <LimitationsDisclosuresPreview />
        </Box>
      </TabPanel>
    </Box>
  );
};

export default NotesStep;
